import React from "react";
import { differenceInDays, format, parse, parseISO, startOfMonth, subMonths } from "date-fns";
import { useRouter } from "next/router";
import { DATE_OPTIONS, DateOptions } from "../../src/utils/constants";
import { groupByOptions, UseGroupBy, useGroupBy } from "./groupBy/useGroupBy";
import {
  TShowGroupByDropdown,
  useShowGroupByDropdown,
} from "./showGroupByDropdown/showGroupByDropdown";
import { getDefaultDateDropdownSettings } from "./utils/getDefaultDateDropdownSettings";
import { useStoresContext } from "../StoresContext";
import { FirstWeekDay } from "../../types";
import {
  getCompareRange,
  getInitialCompareRange,
  getInitialDateRange,
  localStorageCompareRangeKey,
  localStorageDateRangeKey,
} from "./settings";

export type RangeType = { from: string; to: string };
export type CompareRangeType = { prevStartDate: string; prevEndDate: string };

export type DateDropdownContextProps = {
  dateRange: RangeType;
  setSelectedDateIndex: React.Dispatch<React.SetStateAction<DateOptions>>;
  setDateRange: React.Dispatch<React.SetStateAction<any>>;
  selectedDateIndex: number;
  selectedDate: (typeof DATE_OPTIONS)[number];
  beforeNow: number;
  compareRange: CompareRangeType;
  compareSelect: CompareDropdownType;
  changeCompareSelect: React.Dispatch<React.SetStateAction<CompareDropdownType>>;
  setCompareRange: React.Dispatch<React.SetStateAction<CompareRangeType>>;
} & UseGroupBy &
  TShowGroupByDropdown;

export type CompareDropdownType = "previousYear" | "previousPeriod" | "customCompare";

// if the current day is the first day of the month, we change the initial date range from 'DateOptions.MTD' to 'DateOptions.LastMonth'
const isFirstDayOfMonth = new Date().getDate() === 1;
const defaultDateIndex = isFirstDayOfMonth ? DateOptions.LastMonth : DateOptions.MTD;

const DateDropdownContext = React.createContext<DateDropdownContextProps>({
  dateRange: {
    from: format(startOfMonth(new Date()), "yyyy-MM-dd"),
    to: format(new Date(), "yyyy-MM-dd"),
  },
  setSelectedDateIndex: () => {},
  setDateRange: () => {},
  selectedDateIndex: defaultDateIndex,
  selectedDate: DATE_OPTIONS[defaultDateIndex],
  beforeNow: differenceInDays(new Date(), parseISO(format(startOfMonth(new Date()), "yyyy-MM-dd"))),
  compareRange: {
    prevStartDate: format(startOfMonth(subMonths(new Date(), 1)), "yyyy-MM-dd"),
    prevEndDate: format(subMonths(new Date(), 1), "yyyy-MM-dd"),
  },
  compareSelect: "previousPeriod",
  changeCompareSelect: () => {},
  groupByValue: groupByOptions[0].id,
  onGroupByChange: (e: any) => {},
  setGroupByValue: () => {},
  showGroupByDropdown: false,
  setShowGroupByDropdown: () => {},
  setCompareRange: () => {},
});

function DateDropdownProvider(props: any) {
  const { selectedStore } = useStoresContext();
  const currentStore = selectedStore?.id;
  const firstWeekDay = selectedStore?.firstWeekDay || FirstWeekDay.MONDAY;

  const router = useRouter();

  // // Added a check using "allowedStartDate", if a negative value is received in "isOutOfRange" - we set the default value for Date Context
  // const page = React.useMemo(() => {
  //   const pathSegments = router.asPath.split("/").filter(Boolean);
  //   return pathSegments[pathSegments.length - 1] || "order";
  // }, [router.asPath]);
  //
  // const { allowedStartDate } = useBatchRutterSync({
  //   storeId: selectedStore?.id,
  //   page,
  // });
  //
  // React.useEffect(() => {
  //   if (!allowedStartDate) return;
  //   const isOutOfRange = !isInRange(allowedStartDate, new Date(), true);
  //
  //   if (isOutOfRange) {
  //     resetToDefaultDateSettings();
  //   }
  // }, [allowedStartDate]);

  // Date Range
  const [dateRange, setDateRange] = React.useState<RangeType>(getInitialDateRange);

  React.useEffect(() => {
    setDateRange(getInitialDateRange());
  }, [firstWeekDay]);

  React.useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem(localStorageDateRangeKey, JSON.stringify(dateRange));
    }
  }, [dateRange, firstWeekDay]);

  const [compareRange, setCompareRange] = React.useState<CompareRangeType>(getInitialCompareRange);
  const [selectedDateIndex, setSelectedDateIndex] = React.useState(defaultDateIndex);
  const [selectedDate, setSelectedDate] = React.useState<(typeof DATE_OPTIONS)[number]>(
    DATE_OPTIONS[defaultDateIndex],
  );

  const resetToDefaultDateSettings = () => {
    setSelectedDate(DATE_OPTIONS[defaultDateIndex]);
    setSelectedDateIndex(defaultDateIndex);
    setGroupByValue("day");
  };

  // When a user changing the store (storeId) - automatically deletes the keys 'localStorageKey' & 'localStorageCompareKey' from localStorage.
  // Set the default value of 'selectedDate', 'selectedDateIndex' & 'setGroupByValue'
  React.useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.removeItem(localStorageDateRangeKey);
      localStorage.removeItem(localStorageCompareRangeKey);
    }

    resetToDefaultDateSettings();
  }, [currentStore]);

  const [beforeNow, setBeforeNow] = React.useState(
    differenceInDays(new Date(), parseISO(dateRange.from)),
  );
  const { groupByValue, onGroupByChange, setGroupByValue } = useGroupBy();
  const { setShowGroupByDropdown, showGroupByDropdown } = useShowGroupByDropdown();
  const [compareSelect, changeCompareSelect] =
    React.useState<CompareDropdownType>("previousPeriod");

  React.useEffect(
    () => setBeforeNow(differenceInDays(new Date(), parseISO(dateRange.from))),
    [dateRange.from, firstWeekDay],
  );

  React.useEffect(() => {
    if (router.pathname === "/cohorts") {
      setSelectedDateIndex(DateOptions.LastYear);
      setSelectedDate(DATE_OPTIONS[DateOptions.LastYear]);
    }
  }, []);

  React.useEffect(() => {
    if (selectedDateIndex === DateOptions.Custom) {
      setSelectedDate(DATE_OPTIONS[selectedDateIndex]);
      return;
    }
    const range = getDefaultDateDropdownSettings(selectedDateIndex, firstWeekDay);
    setDateRange(range);
    setSelectedDate(DATE_OPTIONS[selectedDateIndex]);
  }, [selectedDateIndex, firstWeekDay]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const changeCompareRange = React.useCallback(
    (newSelectValue: CompareDropdownType) => {
      if (newSelectValue === "customCompare") {
        return;
      }
      const parsedFrom = parse(dateRange.from, "yyyy-MM-dd", new Date());
      const parsedTo = parse(dateRange.to, "yyyy-MM-dd", new Date());

      const newRange = getCompareRange(
        newSelectValue,
        parsedFrom,
        parsedTo,
        selectedDateIndex,
        firstWeekDay,
      );

      if (newRange) {
        setCompareRange(newRange);
      }
    },
    [dateRange.from, dateRange.to, selectedDateIndex, firstWeekDay],
  );

  React.useEffect(() => {
    changeCompareRange(compareSelect);
  }, [changeCompareRange, compareSelect, dateRange, selectedDateIndex, firstWeekDay]);

  // store the date range for the comparative period for "customCompare"
  React.useEffect(() => {
    if (typeof window !== "undefined" && compareSelect === "customCompare") {
      localStorage.setItem(localStorageCompareRangeKey, JSON.stringify(compareRange));
    }
  }, [compareRange, compareSelect]);

  const value = React.useMemo(
    () => ({
      dateRange,
      setSelectedDateIndex,
      setDateRange,
      selectedDateIndex,
      selectedDate,
      beforeNow,
      compareRange,
      compareSelect,
      changeCompareSelect,
      groupByValue,
      onGroupByChange,
      setGroupByValue,
      showGroupByDropdown,
      setShowGroupByDropdown,
      setCompareRange,
    }),
    [
      dateRange,
      setSelectedDateIndex,
      setDateRange,
      selectedDateIndex,
      selectedDate,
      beforeNow,
      compareRange,
      compareSelect,
      changeCompareSelect,
      groupByValue,
      onGroupByChange,
      setGroupByValue,
      showGroupByDropdown,
      setShowGroupByDropdown,
      setCompareRange,
    ],
  );

  return <DateDropdownContext.Provider value={value} {...props} />;
}

function useDateDropdown() {
  const context = React.useContext(DateDropdownContext);

  if (context === undefined) {
    throw new Error("useDateDropdown must be used within an DateDropdownProvider");
  }
  return context;
}

export { DateDropdownProvider, useDateDropdown, DateDropdownContext };
